import MoneyInput from 'components/MoneyInput';
import React from 'react';
import { BooleanInput, NumberInput, useResourceContext, useRecordContext } from 'react-admin';
import {
  isFieldVisible,
  sources,
  type RateSettingsField,
} from 'resources/rate_settings/utils/fieldsVisibility';
import { getInherited } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import { titleize } from 'inflection';
import FacilityAndProfessionalRateInput from '../position-rate-settings-form/LocationAndProfessionalRateInput';
import Heading from 'components/Heading';
import type { InheritedSettingsT } from '../types/inheritedSettings.types';
import useCheckAccess from 'hooks/useCheckAccess';
import { useRateCalculations } from '../position-rate-settings-form/hooks/useRateCalculations';

type ChildrenRateSettingsProps = {
  shouldShowField: (field: RateSettingsField) => boolean;
};

type RateSettingsProps = {
  source: string;
  taxClassification: 'w2.local' | 'w2.travel' | 'ten99';
  inheritedSettings: InheritedSettingsT;
};

type ScalingMarginsProps = Pick<
  RateSettingsProps,
  'source' | 'taxClassification' | 'inheritedSettings'
>;

const RateSettingsSpecificFields = ({
  source,
  shouldShowField,
  taxClassification,
  inheritedSettings,
}: RateSettingsProps & ChildrenRateSettingsProps) => {
  const fields = [];

  sources[taxClassification].forEach((fieldSource) => {
    if (shouldShowField(fieldSource as RateSettingsField)) {
      // TODO: improve
      if (
        fieldSource === 'margin_percent_1099' ||
        fieldSource === 'total_margin_percent_w2' ||
        fieldSource === 'base_margin_percent_w2' ||
        fieldSource === 'margin_percent_w2_travel'
      ) {
        return;
      }

      const FieldComponent = fieldSource.includes('cents') ? MoneyInput : NumberInput;
      fields.push(
        <FieldComponent
          key={fieldSource}
          source={`${source}.${taxClassification}.${fieldSource}`}
          label={titleize(fieldSource)}
          helperText={getInherited(inheritedSettings, `${taxClassification}.${fieldSource}`)}
          blankIfNull
        />,
      );
    }
  });

  return fields;
};

const ScalingMarginsInputs = ({
  source,
  taxClassification,
  inheritedSettings,
}: ScalingMarginsProps) => {
  const fields = [];
  const key = taxClassification.includes('w2') ? 'w2' : '1099';
  const resource = useResourceContext();
  const record = useRecordContext();
  const checkAccess = useCheckAccess();
  const action = record?.id ? 'update' : 'create';
  const isMPRS = resource === 'MarketPositionRateSetting';
  const isLPRS = resource === 'LocationPositionRateSetting';

  if ((taxClassification === 'w2.travel' && !isLPRS) || (!isMPRS && !isLPRS)) {
    return null;
  }

  const marginPercentGuardrailField = `margin_percent_${key}_guardrail`;
  const payoutRatePercentGuardrailField = `payout_rate_percent_${key}_guardrail`;
  const incrementalMarginPercentField = `incremental_margin_percent_${key}`;
  const sourcePrefix = record?.id && source ? source : 'settings';
  const appliedSource = `${sourcePrefix}.${taxClassification}`;

  const { validations } = useRateCalculations({ source: appliedSource, inheritedSettings });

  if (checkAccess(action, resource, marginPercentGuardrailField) && isMPRS) {
    fields.push(
      <NumberInput
        key={marginPercentGuardrailField}
        source={marginPercentGuardrailField}
        label={`Margin Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(inheritedSettings, marginPercentGuardrailField)}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateMarginPercentGuardrail) {
            validations.validateMarginPercentGuardrail(newValue);
          }
        }}
      />,
    );
  }

  if (checkAccess(action, resource, payoutRatePercentGuardrailField) && isMPRS) {
    fields.push(
      <NumberInput
        key={payoutRatePercentGuardrailField}
        source={payoutRatePercentGuardrailField}
        label={`Payout Rate Percent ${key.toUpperCase()} Guardrail`}
        helperText={getInherited(inheritedSettings, payoutRatePercentGuardrailField)}
      />,
    );
  }

  if (checkAccess(action, resource, incrementalMarginPercentField) && (isMPRS || isLPRS)) {
    fields.push(
      <NumberInput
        key={incrementalMarginPercentField}
        source={incrementalMarginPercentField}
        label={`Incremental Margin Percent ${key.toUpperCase()}`}
        helperText={getInherited(inheritedSettings, incrementalMarginPercentField)}
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue) && validations?.validateIncrementalMargin) {
            validations.validateIncrementalMargin(newValue);
          }
        }}
      />,
    );
  }

  return fields;
};

export const RateSettingsTaxClassificationInputs = ({
  source,
  inheritedSettings,
  taxClassification,
  showTitle = false,
}) => {
  const resource = useResourceContext();
  const isW2Travel = taxClassification === 'w2.travel';

  const shouldShowField = (field: RateSettingsField): boolean => {
    return isFieldVisible(field, resource);
  };

  return (
    <>
      {shouldShowField('charge_rate_8hr') && (
        <FacilityAndProfessionalRateInput
          source={`${source}.${taxClassification}`}
          inheritedSettings={inheritedSettings}
        />
      )}

      {RateSettingsSpecificFields({
        shouldShowField,
        source,
        taxClassification,
        inheritedSettings,
      })}

      {ScalingMarginsInputs({
        source,
        taxClassification,
        inheritedSettings,
      })}

      {!isW2Travel && (
        <>
          {showTitle && <Heading>Overtime Settings</Heading>}
          {shouldShowField('payout_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.payout_max_weekly_regular_hours`}
              label="Payout Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_max_weekly_regular_hours`,
              )}
            />
          )}

          {shouldShowField('payout_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.payout_seventh_day_rule_enabled`}
              label="Payout Seventh Day Rule Enabled"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.payout_seventh_day_rule_enabled`,
              )}
            />
          )}

          {shouldShowField('charge_max_weekly_regular_hours') && (
            <NumberInput
              source={`${source}.${taxClassification}.charge_max_weekly_regular_hours`}
              label="Charge Max Weekly Regular Hours"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_max_weekly_regular_hours`,
              )}
            />
          )}

          {shouldShowField('charge_seventh_day_rule_enabled') && (
            <BooleanInput
              source={`${source}.${taxClassification}.charge_seventh_day_rule_enabled`}
              label="Charge Seventh Day Rule Enabled"
              helperText={getInherited(
                inheritedSettings,
                `${taxClassification}.charge_seventh_day_rule_enabled`,
              )}
            />
          )}
        </>
      )}
    </>
  );
};
